/**
 * What goes into a device fingerprint, and what is deliberately left out.
 *
 * Pure, and separate from the browser code that collects the values, because
 * this is the part that can be wrong in a way nobody notices. A fingerprint
 * that includes one unstable component changes on every visit: the table fills
 * with one row per visit, the feature links nothing at all, and from the
 * outside that looks exactly like "there was little to gain here". The
 * serialisation and the exclusions are therefore testable without a browser.
 *
 * WHY THESE COMPONENTS. Chosen for stability against the risk of collision, not
 * copied from anybody:
 *
 * - screen, system, locales, hardware: cheap, stable for months, and weak on
 *   their own. Together they are the backbone.
 * - webgl renderer: the strongest single signal that does not need drawing.
 * - canvas and audio: real discriminating power, and the two that browsers
 *   actively fight. They are OPTIONAL and excluded per browser rather than
 *   used everywhere, because a component that returns randomised output makes
 *   the whole hash unstable, which is worse than not having it.
 *
 * WHAT IS NOT HERE. Fonts, plugins, media devices and permissions: each costs a
 * synchronous measurement on the page and adds little once the rest is present.
 * The user agent string is absent on purpose too: it changes with every browser
 * update, so it would reset every fingerprint roughly monthly.
 */

export interface FingerprintComponents {
  readonly screen?: string;
  readonly system?: string;
  readonly locales?: string;
  readonly hardware?: string;
  readonly webgl?: string;
  readonly canvas?: string;
  readonly audio?: string;
}

/** The components in a fixed order. Order decides the hash, so it is not sorted at runtime. */
const COMPONENT_ORDER: ReadonlyArray<keyof FingerprintComponents> = [
  "screen",
  "system",
  "locales",
  "hardware",
  "webgl",
  "canvas",
  "audio",
];

/**
 * Browsers that lie about a component, per component.
 *
 * Not a blocklist of browsers: a per-component exclusion, because these
 * browsers do not fail to answer, they answer differently every time. Safari
 * has randomised canvas readback since 17, Firefox does it in strict mode, and
 * Brave does it by default. Including such a value would make the fingerprint
 * change per visit while looking perfectly healthy.
 */
export function excludedComponents(userAgent: string, brave: boolean): ReadonlySet<string> {
  const excluded = new Set<string>();
  const ua = userAgent.toLowerCase();

  const isSafari = ua.indexOf("safari") >= 0 && ua.indexOf("chrome") < 0;
  const isFirefox = ua.indexOf("firefox") >= 0;
  const isSamsung = ua.indexOf("samsungbrowser") >= 0;

  if (isSafari || isFirefox || brave) excluded.add("canvas");
  if (isSafari || isSamsung) excluded.add("audio");

  return excluded;
}

/**
 * The string that gets hashed.
 *
 * A component that is missing is written as an empty value rather than skipped,
 * so a browser that gains a component tomorrow does not silently become a
 * different device today. Same reason the order is fixed.
 */
export function serializeComponents(
  components: FingerprintComponents,
  excluded: ReadonlySet<string>,
): string {
  const parts: string[] = [];
  for (const key of COMPONENT_ORDER) {
    if (excluded.has(key)) continue;
    parts.push(`${key}=${components[key] ?? ""}`);
  }
  return parts.join("|");
}

/**
 * Is there enough here to be worth hashing?
 *
 * Two components is a fingerprint that half the visitors of a site share, and a
 * value that collides is worse than no value: it would hand one visitor's
 * session to another person. Below the floor we send nothing, and "nothing" is
 * a state the reports can show honestly.
 */
const MINIMUM_COMPONENTS = 4;

export function hasEnoughEntropy(
  components: FingerprintComponents,
  excluded: ReadonlySet<string>,
): boolean {
  let present = 0;
  for (const key of COMPONENT_ORDER) {
    if (excluded.has(key)) continue;
    const value = components[key];
    if (value !== undefined && value !== "") present += 1;
  }
  return present >= MINIMUM_COMPONENTS;
}
